import { useState } from "react";
import type { ConversationThreadView, ConversationParticipantType } from "../../../../store/types";

type FilterTab = "all" | "customer" | "photographer" | "flagged";

interface Props {
  threads: ConversationThreadView[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onCreateThread: () => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const diffMins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMins < 60) return `${diffMins}m`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
  return d.toLocaleDateString("zh-TW", { month: "numeric", day: "numeric" });
}

const TYPE_LABELS: Record<ConversationParticipantType, { zh: string; color: string }> = {
  customer: { zh: "客戶", color: "bg-emerald-100 text-emerald-700" },
  photographer: { zh: "攝影師", color: "bg-amber-100 text-amber-700" },
  internal: { zh: "內部", color: "bg-[#C8B8A6]/20 text-[#8B7355]" },
};

const RISK_COLOR: Record<string, string> = {
  none: "",
  low: "bg-yellow-50 border-yellow-200",
  medium: "bg-orange-50 border-orange-200",
  high: "bg-red-50 border-red-200",
};

export default function RelayThreadList({ threads, selectedId, onSelect, loading, error, onRetry, onCreateThread }: Props) {
  const [filter, setFilter] = useState<FilterTab>("all");
  const [search, setSearch] = useState("");

  const filtered = threads.filter((t) => {
    if (filter === "flagged") return t.risk_level !== "none";
    if (filter !== "all" && t.participant_type !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.subject.toLowerCase().includes(q) || (t.order_number ?? "").toLowerCase().includes(q) || (t.customer_name ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  const TABS: { key: FilterTab; label: string }[] = [
    { key: "all", label: "全部" },
    { key: "customer", label: "客戶" },
    { key: "photographer", label: "攝影師" },
    { key: "flagged", label: "⚑ 風險" },
  ];

  return (
    <div className="w-72 xl:w-80 flex flex-col border-r border-[#E8E0D5] bg-white shrink-0 h-full">
      <div className="px-4 pt-4 pb-3 border-b border-[#E8E0D5]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-[#2C2C2C] tracking-wide">訊息中心</h2>
          <button onClick={onCreateThread} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#2C2C2C] text-white text-xs font-light hover:bg-[#444] transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line text-xs" /></div>
            新建
          </button>
        </div>
        <div className="relative mb-3">
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center text-[#2C2C2C]/30">
            <i className="ri-search-line text-xs" />
          </div>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜尋訂單、客戶..."
            className="w-full pl-7 pr-3 py-1.5 text-xs font-light border border-[#E8E0D5] rounded-md bg-[#F7F6F3] text-[#2C2C2C] placeholder-[#2C2C2C]/30 focus:outline-none focus:border-[#C8B8A6] text-sm" />
        </div>
        <div className="flex gap-1">
          {TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`flex-1 py-1 text-[10px] font-light rounded-md transition-colors cursor-pointer whitespace-nowrap ${filter === tab.key ? "bg-[#2C2C2C] text-white" : "bg-[#F7F6F3] text-[#2C2C2C]/50 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12"><p className="text-xs text-[#2C2C2C]/30 font-light">載入中...</p></div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center text-red-300">
              <i className="ri-error-warning-line text-2xl" />
            </div>
            <p className="text-xs text-red-500 font-light">{error}</p>
            {onRetry && (
              <button onClick={onRetry} className="text-[11px] text-[#C8B8A6] hover:text-[#2C2C2C] underline cursor-pointer">
                重試
              </button>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
            <div className="w-8 h-8 flex items-center justify-center text-[#2C2C2C]/20 mb-2"><i className="ri-message-3-line text-2xl" /></div>
            <p className="text-xs text-[#2C2C2C]/30 font-light">尚無訊息串</p>
          </div>
        ) : filtered.map((thread) => {
          const typeInfo = TYPE_LABELS[thread.participant_type] ?? TYPE_LABELS.internal;
          const isSelected = thread.id === selectedId;
          const isRisky = thread.risk_level !== "none";
          const unread = (thread.unread_count_admin ?? 0) > 0;
          return (
            <button key={thread.id} onClick={() => onSelect(thread.id)}
              className={`w-full text-left px-4 py-3.5 border-b border-[#F0EAE2] transition-colors cursor-pointer ${isSelected ? "bg-[#F7F6F3]" : `hover:bg-[#FAFAF8] ${isRisky ? RISK_COLOR[thread.risk_level] + " border-l-2" : ""}`}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  {unread && <span className="w-1.5 h-1.5 rounded-full bg-[#C8B8A6] shrink-0 mt-0.5" />}
                  <span className="text-xs font-medium text-[#2C2C2C] truncate">{thread.subject || "（無主旨）"}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isRisky && <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium whitespace-nowrap ${thread.risk_level === "high" ? "bg-red-100 text-red-600" : thread.risk_level === "medium" ? "bg-orange-100 text-orange-600" : "bg-yellow-100 text-yellow-600"}`}>⚑ {thread.risk_level}</span>}
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-light whitespace-nowrap ${typeInfo.color}`}>{typeInfo.zh}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 mb-1">
                {thread.order_number && <span className="text-[10px] text-[#2C2C2C]/40 font-light">{thread.order_number}</span>}
                {thread.customer_name && <><span className="text-[10px] text-[#2C2C2C]/20">·</span><span className="text-[10px] text-[#2C2C2C]/40 font-light truncate">{thread.customer_name}</span></>}
              </div>
              <div className="flex items-center justify-between gap-2">
                {thread.latest_message ? <p className="text-[10px] text-[#2C2C2C]/40 font-light truncate flex-1">{thread.latest_message}</p> : <p className="text-[10px] text-[#2C2C2C]/25 font-light italic">尚無訊息</p>}
                {thread.updated_at && <span className="text-[9px] text-[#2C2C2C]/25 shrink-0">{formatTime(thread.updated_at)}</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
